import React, { useState, useRef, useEffect } from 'react';

/**
 * Generates a responsive srcset string for Supabase Storage images.
 */
function buildSrcSet(src) {
  if (!src || src?.startsWith('data:') || src?.startsWith('blob:')) return undefined;
  if (src?.includes('/storage/v1/object/public/')) {
    const widths = [320, 640, 960, 1280];
    return widths?.map((w) => `${src}?width=${w}&quality=80 ${w}w`)?.join(', ');
  }
  return undefined;
}

/**
 * LazyImage - drop-in <img> replacement with:
 *  - Native lazy loading (loading="lazy")
 *  - IntersectionObserver fallback for older browsers
 *  - Responsive srcset for Supabase Storage URLs
 *  - Blur-up placeholder while loading
 *  - Fallback to no_image.png on error
 */
function LazyImage({
  src,
  alt = 'Image',
  className = '',
  sizes = '(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw',
  eager = false,
  ...props
}) {
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState(false);
  const [inView, setInView] = useState(eager);
  const wrapperRef = useRef(null);

  useEffect(() => {
    if (eager) return;
    // Use native lazy loading if supported
    const supportsNativeLazy = 'loading' in HTMLImageElement.prototype;
    if (supportsNativeLazy) {
      setInView(true);
      return;
    }
    // Fallback: IntersectionObserver
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setInView(true);
          observer.disconnect();
        }
      },
      { rootMargin: '200px' }
    );
    if (wrapperRef?.current) observer?.observe(wrapperRef?.current);
    return () => observer?.disconnect();
  }, [eager]);

  const effectiveSrc = error ? '/assets/images/no_image.png' : src;
  const srcSet = !error ? buildSrcSet(src) : undefined;

  return (
    <span
      ref={wrapperRef}
      className={`inline-block relative overflow-hidden ${className}`}
    >
      {/* Shimmer placeholder */}
      {!loaded && (
        <span
          aria-hidden="true"
          className="absolute inset-0 bg-muted animate-pulse"
          style={{ borderRadius: 'inherit' }}
        />
      )}
      {inView && (
        <img
          src={effectiveSrc}
          srcSet={srcSet}
          sizes={srcSet ? sizes : undefined}
          alt={alt}
          loading={eager ? 'eager' : 'lazy'}
          decoding="async"
          onLoad={() => setLoaded(true)}
          onError={(e) => {
            setError(true);
            setLoaded(true);
            e.target.src = '/assets/images/no_image.png';
          }}
          className={`w-full h-full object-cover transition-opacity duration-300 ${
            loaded ? 'opacity-100' : 'opacity-0'
          }`}
          style={{ display: 'block' }}
          {...props}
        />
      )}
    </span>
  );
}

export default LazyImage;
